/**
 * ===========================================================================
 *  ARENAS — the battlefields, as data
 * ===========================================================================
 *
 * DEV_PLAN Step 9 asks for "a river crossing, a fortress interior and a night
 * raid" that "share every system and none of the geometry". Its premise was
 * that the terrain, base and prop generators were already parameterised. That
 * was one-third true, and measured before anything was built:
 *
 *   bases    YES — bases.js builds by site.type, so a different site list
 *            already yields different architecture.
 *   terrain  NO  — "authored, not noised": the valley normal, the ridge's
 *            endpoints, the mesa and the field plots were constants inside
 *            createTerrain. The seed only varied the noise that dresses them.
 *   layout   NO  — module-level constants, statically imported by eleven
 *            modules across AI, UI, world and game.
 *
 * So a second battlefield could not exist until the battlefield was data. This
 * file is that data. Everything that used to be a constant describing Lembah
 * Brantas now lives in one arena record, and the world reads the ACTIVE record.
 *
 * HOW THE ACTIVE ARENA IS CHOSEN. Terrain is carved once at boot, and layout.js
 * derives RIVER and ROADS (Polylines) at import time. Swapping arena data after
 * import would leave that derived state stale — the proxy-read-in-place-of-the-
 * thing failure this project has paid for repeatedly. So the choice is made
 * INSIDE layout.js's own evaluation, from ?arena= in the URL, before anything
 * is derived. ES module order puts layout.js ahead of every one of its
 * importers, so all eleven see the chosen arena and none can see a stale one.
 * Changing arena is a navigation, which matches how the world is built anyway.
 */

/* ------------------------------------------------------ I. Lembah Brantas */

/**
 * The prototype ground, and the river crossing of Step 9. Every number below
 * is the value that was previously hard-coded in layout.js or terrain.js,
 * moved verbatim — the refactor that introduced this file was proven a no-op
 * by tools/arena-fingerprint.mjs (layout and heightfield hashes identical
 * before and after).
 */
const LEMBAH_BRANTAS = {
  id: 'brantas',
  numeral: 'I',
  name: 'Lembah Brantas',
  kind: 'river crossing',
  stage: 'crossing',          // the default sortie (game/roster.js ALL_STAGES)

  /* The opening battle (ai/director.js seed). Front pairs are
   * [x, z, enemies, allies] with the allied knot 7.5 m to +z; columns
   * [side, x0, z0, x1, z1, n]; officers [side, x, z, retinue, goalX, goalZ].
   * These were literals in director.js and moved verbatim. */
  opening: {
    front: [
      [4, 44, 16, 13], [-22, 30, 13, 11], [30, 56, 14, 10],
      [-50, 50, 12, 9], [46, 22, 11, 9], [-8, 78, 10, 12],
      [0, 4, 13, 10], [-30, -14, 11, 9],
    ],
    cols: [
      ['enemy', 4, -96, 2, -20, 14],
      ['player', 10, 128, 6, 66, 13],
      ['enemy', 118, -32, 40, -8, 12],
      ['player', -150, 40, -60, 12, 12],
      ['enemy', 92, 116, 30, 96, 10],
    ],
    officers: [['enemy', -6, -30, 12, 2, 34], ['player', 14, 86, 10, 6, 46]],
  },
  /* The lines the mission script speaks that belong to THIS ground. They
   * were literals in mission.js, which is how Arena II's first boot told the
   * player the Kidul host had forded a river the arena does not have. */
  story: {
    landing: 'The Kidul host has forded the Brantas',
    hold: 'hold the valley',
    holdLog: 'form on the road and hold the valley',
    won: 'The valley is ours',
  },

  /** The Brantas — a shallow braided river running north→south down the western third. */
  river: [
    [-146, -320], [-132, -238], [-118, -168], [-104, -104], [-94, -46],
    [-88, 6], [-88, 62], [-95, 120], [-108, 182], [-124, 246], [-140, 320],
  ],

  /** Bridge span (on the river, road B crosses here). */
  bridge: { x: -88, z: 27, ax: -103, az: 27.5, bx: -73, bz: 26.5, width: 9 },

  /**
   * Capturable sites. `pad` = radius of flattened ground, `radius` = capture radius.
   * team: 0 = Wilwatikta (player, merah), 1 = Segara Kidul (enemy, indigo),
   * 2 = neutral.
   */
  sites: [
    { id: 'fort_north', name: 'Gapura Wringin',     type: 'fort',    x:    2, z: -172, rot:  0.06,          team: 1, radius: 34, pad: 40, padFeather: 26 },
    { id: 'fort_south', name: 'Benteng Trowulan',   type: 'fort',    x:   14, z:  170, rot:  Math.PI - 0.05, team: 0, radius: 34, pad: 40, padFeather: 26 },
    { id: 'camp_east',  name: 'Pesanggrahan Wetan', type: 'camp',    x:  168, z:  -52, rot: -0.44,          team: 1, radius: 26, pad: 30, padFeather: 22 },
    { id: 'camp_west',  name: 'Pesanggrahan Kulon', type: 'camp',    x: -178, z:   44, rot:  1.32,          team: 0, radius: 26, pad: 30, padFeather: 22 },
    { id: 'bridge',     name: 'Tambangan Brantas',  type: 'bridge',  x:  -88, z:   27, rot:  0,             team: 2, radius: 20, pad: 0,  padFeather: 0 },
    { id: 'hill_post',  name: 'Puncak Kelud',       type: 'post',    x:  124, z:  122, rot: -2.26,          team: 2, radius: 24, pad: 26, padFeather: 26 },
    { id: 'outpost',    name: 'Pos Jatayu',         type: 'outpost', x: -150, z: -110, rot:  0.62,          team: 2, radius: 20, pad: 22, padFeather: 20 },
  ],

  /**
   * THE LANDFORM — the named geography, as opposed to the noise that dresses
   * it (plain() and rim() in terrain.js are generic and stay there). Each of
   * these was a constant inside createTerrain; they are copied here verbatim.
   * An arena with a different shape supplies different numbers, or omits a
   * feature: a missing valley/ridge/mesa is simply not added.
   */
  landform: {
    /** broad valley floor, running WNW→ESE through the arena */
    valley: { nx: -0.34, nz: 0.94, depth: 15.5, width: 128, lift: 3.0, liftWidth: 330 },
    /** long ridge, NE quadrant */
    ridge: { a: [46, -206], b: [246, -34], reach: 150, sigma: 58, height: 38 },
    /** mesa in the SW: flat top, steep skirt */
    mesa: { x: -168, z: -138, hx: 58, hz: 42, rot: -0.38, skirt: 40, height: 24 },
    /** rolling knolls: [cx, cz, radius, height] */
    knolls: [
      [124, 122, 86, 22],     // Puncak Kelud (command post)
      [-46, 152, 66, 10],
      [74, -102, 54, 8],
      [-196, -18, 72, 14],
      [196, 66, 62, 12],
    ],
    /** hand-placed cultivated plots (wheat / millet strips) — pure map dressing */
    fields: [
      { x: -206, z: 92, hx: 30, hz: 20, rot: 0.35, hue: 0 },
      { x: -168, z: 96, hx: 20, hz: 14, rot: 0.35, hue: 1 },
      { x: 198, z: -14, hx: 26, hz: 18, rot: -0.5, hue: 1 },
      { x: 168, z: 16, hx: 18, hz: 22, rot: -0.5, hue: 0 },
      { x: 58, z: 150, hx: 28, hz: 17, rot: 0.18, hue: 1 },
      { x: 108, z: 168, hx: 18, hz: 13, rot: 0.18, hue: 0 },
      { x: -58, z: -166, hx: 24, hz: 16, rot: -0.22, hue: 0 },
      { x: -104, z: -178, hx: 17, hz: 12, rot: -0.22, hue: 1 },
    ],
  },

  /**
   * The staged set-pieces the opening camera is composed around. Their
   * anchors are WORLD positions hand-placed beside this arena's central
   * approaches, with a 0.25 m search radius — art direction for this ground
   * and no other. They lived in battlefield-tableaux.js, and an earlier commit
   * called them "site-local offsets"; they are not, and on Arena II the
   * `last_stand` anchor overlapped the new cross-causeway and threw on boot.
   * `kinds` lists which tableau families belong on this ground.
   */
  tableaux: {
    kinds: ['camp', 'battle', 'field'],
    /* What this arena's dressing must produce. These were exact-equality
     * checks in battlefield-tableaux.js written as if they were universal
     * budgets ("primaryCount !== 5"), and they are not: they are THIS arena's
     * content, pinned. Kept exactly as strict as before — grassExclusions is
     * tableaux.length, so the old pair of checks required exactly 12. */
    expect: { count: [10, 12], primary: 5, grassExclusions: 12 },
    anchors: {
      canvas_field_cache: { x: -17.5, z: -21.5, r: 0.25, a: 0 },
      shield_breach:      { x: 9,     z: -14.5, r: 0.25, a: 0 },
      last_stand:         { x: -10,   z: 24.5,  r: 0.25, a: 0 },
      field_repair_yard:  { x: 14,    z: 26,    r: 0.25, a: 0 },
      grain_spill:        { x: 15,    z: 10,    r: 0.25, a: 0 },
    },
  },

  /**
   * Roadside anchors: where the props pass seeds camps and old battle lines.
   * These were a HARD-CODED copy of this arena's road points inside props.js —
   * missed by the first leftover scan, which only matched `x:`/`z:` keys and
   * not bare coordinate arrays. Moved here verbatim (23 points, hash
   * 7bb7a28a77ec638f before and after) so Arena II cannot inherit them.
   * An arena that omits this gets anchors derived from its own roads.
   */
  roadside: [
      [2, -172], [-6, -124], [6, -70], [-2, -16], [4, 30], [-2, 84],
      [6, 130], [14, 170], [-46, 18], [-16, 6], [16, -2], [64, -14],
      [118, -32], [168, -52], [-150, 40], [-124, 33], [92, 116], [60, 108],
      [34, 98], [-146, -74], [-142, -34], [-146, 6], [-162, 30],
  ],

  /** Road network. Each entry: half-width of the carriageway + shoulder feather. */
  roads: [
    { id: 'A', hw: 3.2, feather: 3.0, pts: [[2, -172], [-6, -124], [6, -70], [-2, -16], [4, 30], [-2, 84], [6, 130], [14, 170]] },
    { id: 'B', hw: 3.0, feather: 2.8, pts: [[-73, 26.5], [-46, 18], [-16, 6], [16, -2], [64, -14], [118, -32], [168, -52]] },
    { id: 'W', hw: 3.0, feather: 2.8, pts: [[-178, 44], [-150, 40], [-124, 33], [-103, 27.5]] },
    { id: 'C', hw: 2.4, feather: 2.4, pts: [[124, 122], [92, 116], [60, 108], [34, 98], [8, 92], [-1, 88]] },
    { id: 'D', hw: 2.2, feather: 2.4, pts: [[-150, -110], [-146, -74], [-142, -34], [-146, 6], [-162, 30], [-178, 44]] },
  ],
};

/* ------------------------------------------------ II. Kraton Wilwatikta */

/**
 * The fortress interior of Step 9. The inner compound of the Majapahit kraton
 * at Trowulan: paved courts, gatehouses and an inner keep, fought through from
 * the south gate toward the centre.
 *
 * WHAT MAKES IT A DIFFERENT PLACE, not the same place re-lit — Step 9's bar is
 * "none of the geometry":
 *   - NO RIVER and NO BRIDGE. Lembah Brantas is organised around a river; this
 *     ground is organised around walls. `river: []` and `bridge: null` are the
 *     honest encoding, and every consumer has to survive them.
 *   - NO VALLEY, NO RIDGE. Flat paved courts rather than a landscape.
 *   - A RAISED CENTRE. The keep stands on a platform (the mesa feature, put to
 *     a different use): the fight climbs INTO the compound rather than across it.
 *   - NO FIELDS. It is stone, not farmland.
 *   - Its sites form one enclosed complex rather than seven holdings scattered
 *     across a valley.
 */
const KRATON_WILWATIKTA = {
  id: 'kraton',
  numeral: 'II',
  name: 'Kraton Wilwatikta',
  kind: 'fortress interior',
  stage: 'kraton',

  /* The fight is at the gates and in the courts. Every point here is clear of
   * the walls by more than a knot's scatter (checked by walls-check), and the
   * alun-alun pair sits on the default landing so the opening frame has a
   * battle in it. Its crossings are its main gates: the kraton's version of
   * the Brantas's contested bridge. */
  opening: {
    front: [
      [0, 40, 14, 12], [-30, 72, 12, 10], [30, 72, 12, 10],
      [0, 126, 16, 13], [-140, 126, 13, 11], [140, 126, 13, 11],
      [-104, 60, 12, 10], [104, 60, 12, 10],
    ],
    cols: [
      ['enemy', 0, -36, 0, 22, 14],
      ['player', 0, 172, 0, 128, 13],
      ['enemy', 150, -66, 150, -6, 12],
      ['player', -140, 92, -104, 34, 12],
      ['enemy', -150, -66, -150, -6, 10],
    ],
    officers: [['enemy', 0, -40, 12, 0, 40], ['player', 0, 150, 10, 0, 64]],
    /* [enemyX, enemyZ, allyX, allyZ, enemies, allies]: the Kidul side of each
     * main gate is the inner one, the way the host holds the inner courts. */
    crossings: [
      [0, 96, 0, 124, 14, 12], [0, -34, 0, -6, 14, 12],
      [-76, 18, -48, 18, 13, 11], [76, 18, 48, 18, 13, 11],
    ],
  },
  story: {
    landing: 'The Kidul host is inside the Gapura Agung',
    hold: 'hold the courts',
    holdLog: 'form in the forecourt and hold the courts',
    won: 'The kraton is ours',
  },

  river: [],
  bridge: null,

  sites: [
    { id: 'keep',        name: 'Siti Hinggil',       type: 'fort', x:    0, z:  -96, rot:  0,          team: 1, radius: 34, pad: 44, padFeather: 20 },
    { id: 'gate_south',  name: 'Gapura Bajang Ratu', type: 'post', x:    0, z:  150, rot:  Math.PI,    team: 0, radius: 22, pad: 26, padFeather: 18 },
    { id: 'court_west',  name: 'Bangsal Kulon',      type: 'camp', x: -104, z:   18, rot:  1.57,       team: 1, radius: 26, pad: 32, padFeather: 18 },
    { id: 'court_east',  name: 'Bangsal Wetan',      type: 'camp', x:  104, z:   18, rot: -1.57,       team: 1, radius: 26, pad: 32, padFeather: 18 },
    { id: 'gate_west',   name: 'Regol Kulon',        type: 'post', x: -150, z: -104, rot:  1.57,       team: 2, radius: 20, pad: 24, padFeather: 18 },
    { id: 'gate_east',   name: 'Regol Wetan',        type: 'post', x:  150, z: -104, rot: -1.57,       team: 2, radius: 20, pad: 24, padFeather: 18 },
  ],

  /** Paved causeways, wide and straight: this ground was laid out, not worn.
   *  Every one of them passes a wall through one of its gates — a road that
   *  meets a wall anywhere else is a road nobody could have walked. */
  roads: [
    { id: 'axis',  hw: 4.2, feather: 2.0, pts: [[0, 200], [0, 150], [0, 110], [0, 30], [0, -20], [0, -56], [0, -96]] },
    { id: 'cross', hw: 3.6, feather: 2.0, pts: [[-104, 18], [-62, 18], [0, 18], [62, 18], [104, 18]] },
    { id: 'wwall', hw: 3.0, feather: 2.0, pts: [[-104, 18], [-150, 2], [-150, -20], [-150, -60], [-150, -104]] },
    { id: 'ewall', hw: 3.0, feather: 2.0, pts: [[104, 18], [150, 2], [150, -20], [150, -60], [150, -104]] },
    { id: 'wsouth', hw: 3.0, feather: 2.0, pts: [[-104, 18], [-140, 70], [-140, 110], [-96, 150], [0, 150]] },
    { id: 'esouth', hw: 3.0, feather: 2.0, pts: [[104, 18], [140, 70], [140, 110], [96, 150], [0, 150]] },
  ],

  /**
   * The kraton's walls: what makes this a fortress INTERIOR rather than a
   * field with forts on it. Read by `src/world/walls.js` (collision, line of
   * sight, gate routing) and built by `src/world/bases.js`.
   *
   * Each line is a polyline traced so a run's outward normal n = (-uz, ux)
   * points toward the side the parapet faces — for the curtain, OUT of the
   * compound (the same convention as `wallRun`); for a dividing wall, toward
   * the side the player approaches from, so the face he fights in front of
   * is the crenellated one. A gate is a point on a line
   * and a clear width; the line is cut there and a gate structure stands in
   * the gap. `bounds` marks the curtain: nothing spawns outside it.
   *
   *   z = -208  +------------+----------------------+------------+
   *             | Kulon wing |  inner north court   | Wetan wing |
   *             |  Regol K.  | [ Siti Hinggil     ] |  Regol W.  |
   *             |           [g] terrace, 7 m up   [g]            |
   *   z =  -20  +----[g]-----+---------[g]----------+----[g]-----+
   *             | west court |      alun-alun       | east court |
   *             |  Bangsal  [g]                    [g]  Bangsal  |
   *   z =  110  +----[g]-----+---------[g]----------+----[g]-----+
   *             |                 forecourt                      |
   *   z =  208  +---------------------[G]------------------------+
   */
  /* What grows. A palace compound is not a wood: inside the curtain only the
   * families listed may grow, and every other one grows outside the walls or
   * nowhere. The alun-alun's pair of waringin — the sacred banyans a Javanese
   * court plants in its own square — are placed, not scattered. */
  flora: {
    inside: ['banyan'],
    waringin: [[-17, 44], [17, 44]],
  },

  walls: {
    lines: [
      { id: 'curtain', bounds: true, closed: true, h: 9.6, t0: 4.4, t1: 3.1,
        pts: [[-208, 208], [208, 208], [208, -208], [-208, -208]],
        gates: [{ at: [0, 208], w: 12, kind: 'paduraksa', name: 'Gapura Agung' }] },
      { id: 'kori', h: 7.2, t0: 3.4, t1: 2.5,
        pts: [[-208, 110], [208, 110]],
        gates: [
          { at: [-140, 110], w: 10, kind: 'bentar' },
          { at: [0, 110], w: 12, kind: 'bentar' },
          { at: [140, 110], w: 10, kind: 'bentar' },
        ] },
      { id: 'srimanganti', h: 7.2, t0: 3.4, t1: 2.5,
        pts: [[-208, -20], [208, -20]],
        gates: [
          { at: [-150, -20], w: 10, kind: 'bentar' },
          { at: [0, -20], w: 12, kind: 'bentar' },
          { at: [150, -20], w: 10, kind: 'bentar' },
        ] },
      { id: 'bangsal_w', h: 6.4, t0: 3.2, t1: 2.3, pts: [[-62, 110], [-62, -20]],
        gates: [{ at: [-62, 18], w: 10, kind: 'bentar' }] },
      { id: 'bangsal_e', h: 6.4, t0: 3.2, t1: 2.3, pts: [[62, -20], [62, 110]],
        gates: [{ at: [62, 18], w: 10, kind: 'bentar' }] },
      { id: 'wing_w', h: 7.2, t0: 3.4, t1: 2.5, pts: [[-104, -20], [-104, -208]],
        gates: [{ at: [-104, -104], w: 10, kind: 'bentar' }] },
      { id: 'wing_e', h: 7.2, t0: 3.4, t1: 2.5, pts: [[104, -208], [104, -20]],
        gates: [{ at: [104, -104], w: 10, kind: 'bentar' }] },
      /* The Siti Hinggil's own ring, standing on the terrace top 7 m up. */
      { id: 'hinggil', closed: true, h: 5.8, t0: 3.0, t1: 2.2,
        pts: [[-58, -56], [58, -56], [58, -136], [-58, -136]],
        gates: [{ at: [0, -56], w: 10, kind: 'paduraksa', name: 'Kori Agung' }] },
    ],
  },

  /* Palace courts, not farmland: no `field` tableaux (grain spills, harvest
   * sledges), and none of Lembah Brantas's authored route incidents — those
   * are that arena's story. Camp and battle dressing still grows along this
   * arena's own causeways, through ROADSIDE. */
  /* A range, not a pin: this arena has not been judged yet, and pinning its
   * first boot's count would freeze whatever it happened to produce. */
  tableaux: { kinds: ['camp', 'battle'], anchors: {}, expect: { count: [4, 9], primary: 0 } },

  landform: {
    /* The keep's platform. Lembah Brantas used this feature for a mesa in
     * the far south-west; here it is the raised Siti Hinggil at the heart. */
    mesa: { x: 0, z: -96, hx: 62, hz: 44, rot: 0, skirt: 26, height: 7, flatTop: true },
    knolls: [],
    fields: [],
    /* Made ground. A kraton is laid out on a levelled plain, and its walls
     * are straight runs hundreds of metres long: at Lembah Brantas's roll
     * (measured 5-13 m of rise along every candidate wall line) the footing
     * floats on one end of a run and is buried at the other. */
    plainAmp: 0.12,
    /* The skyline is the curtain wall. The near foothills began at r = 208,
     * INSIDE the curtain's corners (r = 294), and climbed 12 m up them. */
    rimStart: 300,
    /* Lembah Brantas pulls its central 92 m toward the origin's height so the
     * opening fight is on fair ground. This ground is level by construction,
     * and the pull was lowering the terrace's south edge. */
    centreFlatten: 0,
    /* What the ground is made of where it is not grass, later entries on
     * top. Everything inside the curtain is laid stone; the alun-alun, the
     * great square between the courts, is raked sand, which is what a
     * Javanese alun-alun is and what keeps the heart of the map from being
     * one grey sheet. Albedo, material and the grass mask all read this. */
    ground: [
      { kind: 'pave', x0: -208, z0: -208, x1: 208, z1: 208, feather: 2.5 },
      { kind: 'sand', x0: -62, z0: -20, x1: 62, z1: 110, feather: 3.5 },
    ],
  },
};


/* ============================================================ Arena III */

/**
 * ALAS PURWO — a night raid on the Kidul war camp.
 *
 * Nothing of Lembah Brantas or the kraton: a raised plateau in rolling forest,
 * the camp on its top behind a TIMBER stockade (walls.js material 'timber')
 * whose line follows the plateau's chamfered edge at 8 m in, and an inner
 * ring round the command pavilion. The player comes up out of the trees from
 * the south. Holdings are this arena's own kinds — bivouacs, a pendapa, and
 * watch towers in the forest — not the forts and camps of the other two.
 * Each site's `type` is its ROLE (the pendapa is the enemy keep, a bivouac a
 * camp, a watch a post — mission, director, minimap and stables read that);
 * `build` is its architecture.
 *
 * Coordinates were computed in the plateau's own frame (rot 0.2 about
 * (0, -40)) and transformed once; every gate lies on its segment and every
 * road passes through its gate (tools/walls-check.mjs holds both).
 */
const ALAS_PURWO = {
  id: 'alas',
  numeral: 'III',
  name: 'Alas Purwo',
  kind: 'night raid',
  stage: 'alas',

  /* A raid comes up out of the trees at the gates: the front pairs are at
   * the south and flank gates and on the slope below them, the Kidul lines
   * inside the stockade, the columns on the forest tracks. */
  opening: {
    front: [
      [-12, 40, 14, 12], [-30, 62, 12, 10], [10, 66, 12, 10],
      [-90, -46, 12, 10], [90, -12, 12, 10], [-4, 96, 11, 12],
      [-24, -8, 12, 9], [30, -20, 11, 9],
    ],
    cols: [
      ['enemy', 0, -34, -8, 4, 14],
      ['player', 0, 150, -8, 80, 13],
      ['enemy', 50, -36, 86, -22, 12],
      ['player', -150, -22, -100, -52, 12],
      ['enemy', -40, -44, -86, -58, 10],
    ],
    officers: [['enemy', 6, -60, 12, -10, 30], ['player', 0, 140, 10, -10, 50]],
    /* A raid map has few long roads, so few road hotspots (4 against the
     * Brantas's 10): its population would sit ~80 short. It fights at the
     * gates and in the treeline, so that is where its knots are. */
    /* The three stockade gates, and the command ring's own: a raid that has
     * got inside is fighting for the pendapa's gate. Alas Purwo streams the
     * fewest men of the three in its first seconds (measured +11 over its seed
     * against +51 for the Brantas), so its seed carries the margin itself. */
    crossings: [
      [-9, 2, -15, 34, 16, 14], [58.8, -28.1, 90, -22, 14, 12], [-58.8, -51.9, -90, -58, 14, 12],
      [3, -57, 0.4, -38, 12, 10],
    ],
    hotspots: [[-60, 118, 12, 'field'], [60, 108, 12, 'field'], [-128, 40, 12, 'field'], [128, 12, 12, 'field']],
  },
  story: {
    landing: 'The Kidul war camp sleeps behind its stockade',
    hold: 'hold the treeline',
    holdLog: 'form in the treeline and wait for the signal',
    won: 'The Kidul camp burns',
  },

  river: [],
  bridge: null,

  sites: [
    { id: 'command',   name: 'Pendapa Kidul',     type: 'fort', build: 'pendapa', x:    6.0, z:  -69.4, rot: 0.2,        team: 1, radius: 20, pad: 22, padFeather: 12 },
    { id: 'bivouac_w', name: 'Pakuwon Kulon',     type: 'camp', build: 'bivouac', x:  -45.1, z:  -49.1, rot: 0.2 + 1.57, team: 1, radius: 18, pad: 20, padFeather: 12 },
    { id: 'bivouac_e', name: 'Pakuwon Wetan',     type: 'camp', build: 'bivouac', x:   46.7, z:  -38.7, rot: 0.2 - 1.57, team: 1, radius: 18, pad: 20, padFeather: 12 },
    { id: 'stores',    name: 'Lumbung',           type: 'camp', build: 'bivouac', stores: true, x:   -5.2, z:  -14.5, rot: 0.2 + 3.14, team: 1, radius: 16, pad: 18, padFeather: 10 },
    { id: 'watch_s',   name: 'Panggung Kidul',    type: 'post', build: 'watch', x:    0,   z:  150,   rot: 3.14,       team: 0, radius: 14, pad: 16, padFeather: 12 },
    { id: 'watch_w',   name: 'Panggung Kulon',    type: 'post', build: 'watch', x: -160,   z:  -20,   rot: 1.57,       team: 2, radius: 14, pad: 16, padFeather: 12 },
    { id: 'watch_e',   name: 'Panggung Wetan',    type: 'post', build: 'watch', x:  160,   z:  -60,   rot: -1.57,      team: 2, radius: 14, pad: 16, padFeather: 12 },
  ],

  /** Forest tracks, not causeways: narrow and worn. */
  roads: [
    { id: 'ascent', hw: 2.8, feather: 2.4, pts: [[0, 150], [-8, 90], [-14.3, 30.6], [-8.7, 3.1], [-5.2, -14.5], [0.4, -42.0], [6.0, -69.4]] },
    { id: 'west',   hw: 2.4, feather: 2.4, pts: [[-160, -20], [-120, -40], [-90.2, -58.3], [-58.8, -51.9], [-45.1, -49.1]] },
    { id: 'east',   hw: 2.4, feather: 2.4, pts: [[160, -60], [120, -40], [90.2, -21.7], [58.8, -28.1], [46.7, -38.7]] },
  ],

  tableaux: { kinds: ['camp', 'battle'], anchors: {}, expect: { count: [4, 9], primary: 0 } },

  /* The camp is cleared ground; the forest is everything else, and thick —
   * a night raid comes out of trees. Counts are multiples of the families'
   * shared defaults; nothing grows inside the stockade. */
  flora: {
    inside: [],
    scale: { palm: 1.8, broadleaf: 3.0, bamboo: 2.2, banana: 1.4, deadtree: 0.5, fence: 0, reed: 0 },
    // the first capture's woods stood 120-250 m out and read as parkland:
    // pull them to the hill's foot and plant more of them
    forestRing: [96, 150],
    forestClusters: 22,
  },

  walls: {
    lines: [
      { id: 'stockade', bounds: false, closed: true, material: 'timber', h: 5.4, t0: 1.1, t1: 1.0,
        pts: [[-66.4, 5.7], [43.4, 28.0], [66.9, 12.3], [82.0, -62.1], [66.4, -85.7], [-43.4, -108.0], [-66.9, -92.3], [-82.0, -17.9]],
        gates: [
          { at: [-11.5, 16.8], w: 9, kind: 'regol', name: 'Regol Kidul' },
          { at: [74.5, -24.9], w: 7, kind: 'regol' },
          { at: [-74.5, -55.1], w: 7, kind: 'regol' },
        ] },
      { id: 'kedaton', closed: true, material: 'timber', h: 4.6, t0: 1.0, t1: 0.9,
        pts: [[-11.3, -49.9], [14.2, -44.8], [31.4, -64.2], [23.2, -88.9], [-2.3, -94.0], [-19.5, -74.6]],
        gates: [{ at: [1.5, -47.4], w: 7, kind: 'regol' }] },
    ],
  },

  landform: {
    /* The old hill the camp sits on: a flat top 12 m above the forest floor,
     * rotated so the stockade does not square up with the world axes. */
    mesa: { x: 0, z: -40, hx: 84, hz: 66, rot: 0.2, skirt: 44, height: 12, flatTop: true },
    knolls: [[-150, 96, 74, 16], [164, 112, 84, 20], [-172, -150, 92, 24], [172, -142, 80, 22], [40, 196, 62, 9], [-60, 200, 56, 7]],
    fields: [],
    plainAmp: 0.9,
    /* The fair-ground pull would drag the plateau's southern lip toward the
     * origin's height, exactly as it did the kraton's terrace. */
    centreFlatten: 0,
    /* The camp's floor, trodden to earth: the stockade's bounding box and a
     * margin, so the ground outside the logs is cleared too — a camp that
     * expects an attack cuts its field of view back from the palisade. */
    ground: [{ kind: 'dirt', x0: -92, z0: -116, x1: 92, z1: 36, feather: 8 }],
  },
};

/* ------------------------------------------------------------- registry */

export const ARENA_DEFS = [LEMBAH_BRANTAS, KRATON_WILWATIKTA, ALAS_PURWO];

export const DEFAULT_ARENA = 'brantas';

/**
 * The arena a URL names, or the default. An unknown id falls back LOUDLY: a
 * silent fallback that is indistinguishable from a correct value is how the
 * glaive's trail got drawn on two weapons that had no row of their own.
 */
export function resolveArena(search) {
  let id = DEFAULT_ARENA;
  try {
    const q = new URLSearchParams(search || '');
    if (q.has('arena')) id = q.get('arena');
  } catch (_) { /* no URL: the default */ }
  const hit = ARENA_DEFS.find((a) => a.id === id);
  if (!hit) {
    console.warn(`[arenas] unknown arena "${id}" — falling back to "${DEFAULT_ARENA}". ` +
      `Known: ${ARENA_DEFS.map((a) => a.id).join(', ')}`);
    return ARENA_DEFS.find((a) => a.id === DEFAULT_ARENA);
  }
  return hit;
}
